import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ResourceDetailDrawer } from "@/components/dashboard/ResourceDetailDrawer";
import {
  type ResourceFilterOptions,
  ResourcesFilterBar,
} from "@/components/dashboard/resources-filters/ResourcesFilterBar";
import { DisciplineChip } from "@/components/shared/discipline-chip";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { ReservationBadge } from "@/components/shared/ReservationBadge";
import { useResourceFilters } from "@/hooks/use-resource-filters";
import {
  CONSTRAINT_GROUP_ORDER,
  constraintCellBackground,
  constraintGroupHue,
} from "@/lib/constraint-colors";
import { jsonFetch } from "@/lib/json-fetch";
import { resourceDirectoryKeys } from "@/lib/query";
import { matchesResourceFilters } from "@/lib/resource-filters";
import type { ResourceReservationView } from "@/schemas/resource-reservations";

interface ResourceCard {
  id: string;
  name: string;
  email: string;
  initials: string;
  capacity: number;
  points: number;
  active: boolean;
  constraints: Array<{ id: string; name: string; group: string; rating: number | null }>;
  disciplines: Array<{ id: string; code: string; name: string }>;
  solutions: Array<{ id: string; name: string }>;
  accounts: Array<{ id: string; name: string }>;
  // The Account reservation on this Resource, if any. An expired reservation (`active: false`) is not
  // a reservation — render and filter on `active`, never on presence alone.
  reservation: ResourceReservationView | null;
}

// Compact capacity pill: load over capacity, tinted by utilisation. Structural
// read of bench load — over-allocation goes red, near-full amber, otherwise navy.
function CapacityPill({ used, capacity }: { used: number; capacity: number }) {
  const pct = capacity === 0 ? 0 : Math.min(100, (used / capacity) * 100);
  const over = used > capacity;
  const tone = over ? "var(--fit-at-risk)" : pct > 85 ? "var(--fit-moderate)" : "var(--brand-600)";
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="font-display text-sm font-bold leading-none tabular-nums">
        {used}
        <span className="text-xs font-semibold text-muted-foreground">/{capacity}</span>
      </span>
      <span className="h-1 w-10 overflow-hidden rounded-full bg-muted">
        <span
          className="block h-full rounded-full"
          style={{ width: `${pct}%`, background: tone }}
        />
      </span>
    </div>
  );
}

// The per-row density block: one cluster per Constraint group, each a wrapped grid
// of intensity cells (hue = group, alpha = rating). This is what makes the bench
// read as a matrix when scanned vertically.
function ConstraintDensity({ constraints }: { constraints: ResourceCard["constraints"] }) {
  const byGroup = useMemo(() => {
    const map = new Map<string, ResourceCard["constraints"]>();
    for (const g of CONSTRAINT_GROUP_ORDER) map.set(g, []);
    for (const c of constraints) {
      const bucket = map.get(c.group);
      if (bucket) bucket.push(c);
    }
    // Strongest ratings first; unrated sink to the end of each cluster.
    for (const bucket of map.values()) {
      bucket.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
    }
    return map;
  }, [constraints]);

  if (constraints.length === 0) {
    return <span className="text-xs text-muted-foreground">No ratings yet</span>;
  }

  return (
    <div className="flex flex-col gap-[3px]">
      {CONSTRAINT_GROUP_ORDER.map((g) => {
        const items = byGroup.get(g) ?? [];
        if (items.length === 0) return null;
        const hue = constraintGroupHue(g);
        return (
          <div key={g} className="flex flex-wrap gap-[3px]">
            {items.map((c) => (
              <span
                key={c.id}
                className="h-3.5 w-3.5 rounded-[3px] transition-transform hover:scale-125"
                title={`${g} · ${c.name}: ${c.rating ?? "unrated"}`}
                style={
                  c.rating == null
                    ? { boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 25%, transparent)` }
                    : { background: constraintCellBackground(g, c.rating) }
                }
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function ResourcesPage() {
  const { state: filters, setState: setFilters, reset: resetFilters } = useResourceFilters();
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useQuery<{ resources: ResourceCard[] }>({
    queryKey: resourceDirectoryKeys.all,
    queryFn: () => jsonFetch<{ resources: ResourceCard[] }>("/api/dashboard/resources"),
  });

  // Derive the available option sets from the loaded bench so every control reflects
  // the directory's actual coverage.
  const options = useMemo<ResourceFilterOptions>(() => {
    const disciplines = new Map<string, { code: string; name: string }>();
    const constraints = new Map<string, { id: string; name: string }>();
    const solutions = new Map<string, { id: string; name: string }>();
    const accounts = new Map<string, { id: string; name: string }>();
    let pointsCeiling = 0;
    for (const r of data?.resources ?? []) {
      for (const d of r.disciplines) if (!disciplines.has(d.code)) disciplines.set(d.code, d);
      for (const c of r.constraints)
        if (!constraints.has(c.id)) constraints.set(c.id, { id: c.id, name: c.name });
      for (const s of r.solutions) if (!solutions.has(s.id)) solutions.set(s.id, s);
      for (const a of r.accounts) if (!accounts.has(a.id)) accounts.set(a.id, a);
      pointsCeiling = Math.max(pointsCeiling, r.points, r.capacity);
    }
    const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
    return {
      disciplines: [...disciplines.values()].sort((a, b) => a.code.localeCompare(b.code)),
      constraints: [...constraints.values()].sort(byName),
      solutions: [...solutions.values()].sort(byName),
      accounts: [...accounts.values()].sort(byName),
      pointsCeiling: pointsCeiling || 1,
    };
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return (data.resources ?? []).filter((r) => matchesResourceFilters(r, filters));
  }, [data, filters]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Manage"
        title="Resources"
        description="The bench — constraints, Disciplines, and current load."
        actions={
          <ResourcesFilterBar
            state={filters}
            setState={setFilters}
            reset={resetFilters}
            options={options}
          />
        }
      />

      {isLoading && (
        <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      )}

      {error && !isLoading && (
        <ErrorState message="Failed to load resources." onRetry={() => refetch()} />
      )}

      {data && (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {/* Column header — mirrors the density-matrix mock. */}
          <div className="grid grid-cols-[minmax(200px,1.4fr)_72px_minmax(96px,0.6fr)_minmax(280px,2fr)_64px] items-center gap-3 border-b border-border bg-secondary px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <span>Resource</span>
            <span className="text-center">Capacity</span>
            <span>Discipline</span>
            <span>Constraint density</span>
            <span className="text-right">Rated</span>
          </div>

          <div className="divide-y divide-border">
            {filtered.map((r) => {
              const rated = r.constraints.filter((c) => c.rating != null);
              const ratedCount = rated.length;
              const score = rated.reduce((sum, c) => sum + (c.rating ?? 0), 0);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setOpenId(r.id)}
                  className="grid w-full grid-cols-[minmax(200px,1.4fr)_72px_minmax(96px,0.6fr)_minmax(280px,2fr)_64px] items-center gap-3 px-4 py-2.5 text-left transition hover:bg-brand-50/60"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground/85 text-sm font-semibold text-background">
                      {r.initials}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-display text-sm font-semibold leading-tight">
                        {r.name}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.email}
                      </span>
                      {r.reservation?.active && (
                        <ReservationBadge
                          accountName={r.reservation.accountName}
                          until={r.reservation.until}
                          className="mt-1"
                        />
                      )}
                    </span>
                  </div>

                  <div className="flex justify-center">
                    <CapacityPill used={r.points} capacity={r.capacity} />
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {r.disciplines.length === 0 ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      r.disciplines.map((d) => (
                        <DisciplineChip key={d.id} code={d.code} title={d.name} />
                      ))
                    )}
                  </div>

                  <ConstraintDensity constraints={r.constraints} />

                  <div className="flex flex-col items-end leading-tight">
                    <span className="font-display text-sm font-bold tabular-nums">
                      {ratedCount}
                    </span>
                    <span
                      className="text-xs uppercase tracking-wide text-muted-foreground tabular-nums"
                      title="Sum of confirmed Constraint ratings"
                    >
                      Σ {score}
                    </span>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                No matches.
              </div>
            )}
          </div>
        </div>
      )}

      {openId && <ResourceDetailDrawer id={openId} onClose={() => setOpenId(null)} />}
    </PageContainer>
  );
}
